
import React, { useEffect, useState } from 'react';
import { TaskProposal } from '../types';
import apiService from '../../../../services/api';
import { useNotification } from '../../../../contexts/NotificationContext';
import { useConfirm } from '../../../../contexts/ConfirmContext';
import { useAuth } from '../../../../contexts/AuthContext';
import SidePanel from '../../../UI/SidePanel';
import styles from '../../../../styles/TaskDetails.module.css';
import buttonStyles from '../../../../styles/Buttons.module.css';
import sidePanelStyles from '../../../../styles/SidePanel.module.css';
import SkeletonLoader from '../../../UI/SkeletonLoader';

interface ProposalDetailsPanelProps {
    proposalId: string;
    onClose: () => void;
    onUpdate: () => void;
    isOpen: boolean;
}

const ProposalDetailsPanel: React.FC<ProposalDetailsPanelProps> = ({
    proposalId,
    onClose,
    onUpdate,
    isOpen
}) => {
    const { user } = useAuth();
    const notificationService = useNotification();
    const confirmService = useConfirm();
    const [proposal, setProposal] = useState<TaskProposal | null>(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [comments, setComments] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);

    useEffect(() => {
        if (isOpen && proposalId) {
            loadProposalDetails();
        } else {
            setProposal(null); // Reset when closed
        }
    }, [isOpen, proposalId]);

    const loadProposalDetails = async () => {
        try {
            setLoading(true);
            const response = await apiService.getTaskProposalDetails(proposalId);
            if (response && (response as any).id) {
                setProposal(response as any);
            } else if (response.data) {
                setProposal(response.data);
            }
        } catch (error: any) {
            console.error('Error loading proposal details:', error);
            notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load proposal details');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!proposal) return;

        const confirmed = await confirmService.show(
            'Approve Proposal',
            'Are you sure you want to approve this proposal?',
            { okText: 'Approve', cancelText: 'Cancel' }
        );

        if (confirmed) {
            try {
                setActionLoading(true);
                await apiService.approveProposal(proposal.id, comments);
                notificationService.success('Proposal approved successfully');
                onUpdate();
                onClose();
            } catch (error: any) {
                console.error('Error approving proposal:', error);
                notificationService.error(error.response?.data?.error || 'Failed to approve proposal');
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleReject = async () => {
        if (!proposal) return;
        if (!comments.trim()) {
            notificationService.error('Please provide a reason for rejection in the comments.');
            return;
        }

        const confirmed = await confirmService.show(
            'Reject Proposal',
            'Are you sure you want to reject this proposal?',
            { okText: 'Reject', cancelText: 'Cancel' }
        );

        if (confirmed) {
            try {
                setActionLoading(true);
                await apiService.rejectProposal(proposal.id, comments);
                notificationService.success('Proposal rejected successfully');
                onUpdate();
                onClose();
            } catch (error: any) {
                console.error('Error rejecting proposal:', error);
                notificationService.error(error.response?.data?.error || 'Failed to reject proposal');
            } finally {
                setActionLoading(false);
            }
        }
    };

    // Determine if current user can approve/reject
    const canAct = React.useMemo(() => {
        if (!proposal || !user) return false;

        // 1. Strict Check: Has user already acted?
        const hasActed = (proposal.approval_data || []).some((entry: any) =>
            String(entry.approver_id) === String(user.id)
        );
        if (hasActed) return false;

        if (proposal.status !== 'pending_approval') return false;
        if (user.role === 'admin' || (user as any).is_superuser) return true;

        if (proposal.current_approver) {
            return String(proposal.current_approver) === String(user.id);
        }

        return false;

    }, [proposal, user]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getTimelineIcon = (action: string) => {
        if (action === 'approved') {
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                </svg>
            );
        } else if (action === 'rejected') {
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                </svg>
            );
        } else if (action === 'forwarded') {
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="12" y1="18" x2="12" y2="12" />
                    <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
            );
        } else if (action === 'withdrawn') {
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="8" y1="12" x2="16" y2="12" />
                </svg>
            );
        } else {
            // Submitted / Default
            return (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14,2 14,8 20,8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                </svg>
            );
        }
    };

    const handleForward = async () => {
        if (!proposal) return;

        const confirmed = await confirmService.show(
            'Forward Proposal',
            'Are you sure you want to forward this proposal to the next level?',
            { okText: 'Forward', cancelText: 'Cancel' }
        );

        if (confirmed) {
            try {
                setActionLoading(true);
                await apiService.forwardProposal(proposal.id, comments);
                notificationService.success('Proposal forwarded successfully');
                onUpdate();
                onClose();
            } catch (error: any) {
                console.error('Error forwarding proposal:', error);
                notificationService.error(error.response?.data?.error || 'Failed to forward proposal');
            } finally {
                setActionLoading(false);
            }
        }
    };

    return (
        <SidePanel
            isOpen={isOpen}
            onClose={onClose}
            title="Proposal Details"
        >
            {loading ? (
                <div style={{ padding: '0 1.5rem', marginTop: '1.5rem' }}>
                    {/* Proposal Info Skeleton */}
                    <div className={sidePanelStyles.formGroup}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <SkeletonLoader variant="custom" width="150px" height="20px" />
                            <div style={{ width: '80px', height: '24px', borderRadius: '12px', overflow: 'hidden' }}>
                                <SkeletonLoader variant="custom" width="100%" height="100%" />
                            </div>
                        </div>
                        <div className={styles.stackedDetails}>
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.8rem' }}>
                                    <SkeletonLoader variant="custom" width="100px" height="16px" />
                                    <SkeletonLoader variant="custom" width="150px" height="16px" />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Description Skeleton */}
                    <div className={sidePanelStyles.formGroup} style={{ marginTop: '2rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <SkeletonLoader variant="custom" width="100px" height="20px" />
                        </div>
                        <SkeletonLoader variant="custom" width="100%" height="80px" />
                    </div>

                    {/* Timeline Skeleton */}
                    <div className={sidePanelStyles.formGroup} style={{ marginTop: '2rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <SkeletonLoader variant="custom" width="100px" height="20px" />
                        </div>
                        {[1, 2].map((i) => (
                            <div key={i} style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0, marginTop: '5px' }}>
                                    <SkeletonLoader variant="custom" width="100%" height="100%" />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ marginBottom: '0.5rem' }}>
                                        <SkeletonLoader variant="custom" width="60%" height="16px" />
                                    </div>
                                    <SkeletonLoader variant="custom" width="40%" height="14px" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : proposal ? (
                <div className={sidePanelStyles.form} style={{ padding: '0 1.5rem' }}>
                    {/* Proposal Information */}
                    <div className={sidePanelStyles.formGroup}>
                        <div className={sidePanelStyles.sectionHeader} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 className={sidePanelStyles.sectionTitleSmall} style={{ marginBottom: 0 }}>Proposal Information</h4>
                            <span className={`statusBadge ${(proposal.display_status === 'approved' || proposal.status === 'approved')
                                ? styles.statusApproved
                                : proposal.status === 'rejected' ? styles.statusRejected
                                    : styles.statusPendingApproval}`}
                                style={{ padding: '0.2rem 0.6rem', borderRadius: '12px', color: 'white', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase' }}>
                                {
                                    (proposal.display_status === 'approved' || proposal.status === 'approved')
                                        ? 'Approved'
                                        : proposal.status.replace('_', ' ')
                                }
                            </span>
                        </div>

                        <div className={styles.stackedDetails}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Title</div>
                                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>{proposal.title}</div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Submitted by</div>
                                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>{proposal.requester_details?.full_name || 'Unknown'}</div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Submitted on</div>
                                <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>{formatDate(proposal.created_at)}</div>
                            </div>

                            {proposal.group && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Department</div>
                                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827' }}>{(proposal.group as any).name || 'N/A'}</div>
                                </div>
                            )}

                            {proposal.current_stage && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>Current Stage</div>
                                    <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#111827', textTransform: 'capitalize' }}>{proposal.current_stage.replace(/_/g, ' ')}</div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Description */}
                    {proposal.description && (
                        <div className={sidePanelStyles.formGroup}>
                            <div className={sidePanelStyles.sectionHeader}>
                                <h4 className={sidePanelStyles.sectionTitleSmall}>Description</h4>
                            </div>
                            <div className={styles.notesContent}>
                                <p>{proposal.description}</p>
                            </div>
                        </div>
                    )}

                    {/* Timeline */}
                    <div className={sidePanelStyles.formGroup}>
                        <div className={sidePanelStyles.sectionHeader}>
                            <h4 className={sidePanelStyles.sectionTitleSmall}>Timeline</h4>
                        </div>
                        <div className={styles.timeline}>
                            {(() => {
                                const timelineItems = [
                                    {
                                        stage: 'Proposal Submitted',
                                        action: 'submitted',
                                        approver_name: proposal.requester_details?.full_name || 'Unknown',
                                        timestamp: proposal.created_at,
                                        comments: ''
                                    },
                                    ...(proposal.approval_data || [])
                                ];

                                if (proposal.status === 'withdrawn') {
                                    timelineItems.push({
                                        stage: 'Proposal Withdrawn',
                                        action: 'withdrawn',
                                        approver_name: proposal.requester_details?.full_name || 'Unknown',
                                        timestamp: proposal.updated_at,
                                        comments: ''
                                    });
                                }

                                return timelineItems.map((step, index) => {
                                    const isCurrent = index === timelineItems.length - 1;
                                    let status = 'submitted';
                                    if (step.action === 'approved') status = 'approved';
                                    else if (step.action === 'rejected') status = 'rejected';
                                    else if (step.action === 'withdrawn') status = 'rejected'; // Re-use rejected style (red) or define new one

                                    // Special handle for withdrawn color if needed, otherwise 'rejected' (red) or 'pending' (blue)
                                    const dotClass = step.action === 'withdrawn' ? styles.statusRejected : styles[status] || styles.statusPending;

                                    return (
                                        <div key={index} className={styles.timelineItem}>
                                            <div className={styles.timelineMarker}>
                                                <div className={`${styles.timelineDot} ${dotClass} ${isCurrent ? styles.current : ''}`}></div>
                                                {index < timelineItems.length - 1 && <div className={styles.timelineLine}></div>}
                                            </div>
                                            <div className={styles.timelineContent}>
                                                <div className={styles.timelineTitle}>
                                                    <span className={styles.timelineTitleIcon}>{getTimelineIcon(step.action)}</span>
                                                    {step.stage.replace(/_/g, ' ')}
                                                </div>
                                                <div className={styles.timelineDescription}>
                                                    {step.action} by {step.approver_name}
                                                </div>
                                                <div className={styles.timelineMeta}>
                                                    <span className={styles.timelineUser}>{step.approver_name}</span>
                                                    <span className={styles.timelineDate}>{formatDate(step.timestamp)}</span>
                                                </div>
                                                <div className={`${styles.timelineStatus} ${styles[status] || styles.statusPending}`}>
                                                    {step.action.charAt(0).toUpperCase() + step.action.slice(1)}
                                                </div>
                                                {step.comments && (
                                                    <div className={styles.notesContent} style={{ marginTop: '0.5rem', fontSize: '0.85rem' }}>
                                                        "{step.comments}"
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>
                    </div>

                    {/* Actions */}
                    {canAct && (
                        <>
                            {!showRejectForm ? (
                                <div className={sidePanelStyles.formGroup}>
                                    <div className={sidePanelStyles.sectionHeader}>
                                        <h4 className={sidePanelStyles.sectionTitleSmall}>Actions</h4>
                                    </div>
                                    <div className={sidePanelStyles.formGroup}>
                                        <label className={sidePanelStyles.formLabel}>
                                            Comments (Optional)
                                        </label>
                                        <textarea
                                            className={sidePanelStyles.formControl}
                                            placeholder="Add comments (Optional for approval)"
                                            value={comments}
                                            onChange={(e) => setComments(e.target.value)}
                                            rows={3}
                                        />
                                    </div>
                                    <div className={sidePanelStyles.formActions} style={{ marginBottom: '1rem' }}>
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnGreenSecondary}`}
                                            onClick={handleApprove}
                                            disabled={actionLoading}
                                        >
                                            {actionLoading ? 'Processing...' : 'Approve Proposal'}
                                        </button>
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnWarning}`}
                                            onClick={handleForward}
                                            disabled={actionLoading}
                                            style={{ backgroundColor: '#f59e0b', borderColor: '#f59e0b', color: 'white' }}
                                        >
                                            Forward
                                        </button>
                                        <button
                                            className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
                                            onClick={() => setShowRejectForm(true)}
                                            disabled={actionLoading}
                                        >
                                            Reject Proposal
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className={sidePanelStyles.formGroup}>
                                    <div className={sidePanelStyles.sectionHeader}>
                                        <h4 className={sidePanelStyles.sectionTitleSmall}>Reject Proposal</h4>
                                    </div>
                                    <div className={sidePanelStyles.formGroup}>
                                        <label className={sidePanelStyles.formLabel}>
                                            Reason for Rejection <span className={sidePanelStyles.required}>*</span>
                                        </label>
                                        <textarea
                                            className={sidePanelStyles.formControl}
                                            placeholder="Reason for rejection (Required)"
                                            value={comments}
                                            onChange={(e) => setComments(e.target.value)}
                                            rows={3}
                                            required
                                        />
                                    </div>
                                    <div className={sidePanelStyles.formActions} style={{ marginBottom: '1rem' }}>
                                        <button
                                            type="button"
                                            className={`${buttonStyles.btn} ${buttonStyles.btnSecondary}`}
                                            onClick={() => setShowRejectForm(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={`${buttonStyles.btn} ${buttonStyles.btnDanger}`}
                                            onClick={handleReject}
                                            disabled={actionLoading || !comments.trim()}
                                        >
                                            {actionLoading ? 'Processing...' : 'Confirm Rejection'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            ) : (
                <div className={sidePanelStyles.emptyState}>
                    <div className={sidePanelStyles.emptyIcon}>
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                        </svg>
                    </div>
                    <div className={sidePanelStyles.emptyTitle}>Proposal Not Found</div>
                    <div className={sidePanelStyles.emptyDescription}>The proposal you're looking for could not be found.</div>
                </div>
            )}
        </SidePanel>
    );
};

export default ProposalDetailsPanel;
